// ReportSection — Figma section pattern (e.g. node 6324:134887).
//
// A single bordered, rounded card: a gray header band (leading icon + editable
// DS CanvasSpaceTitle — rename in place, no chevron — + info icon) with a bottom
// divider, above the white metrics body. ONE border around the whole card — the
// body has no border of its own (no second border, no gutter). Reused by every
// report section (Key metrics, Trends, Dimensional analysis).
import { useState, type ReactNode } from 'react'
import { CanvasSpaceTitle, IconButton } from '@clickguard/design-system'
import { Edit05, InfoCircle } from './icons'
import { useReportLayoutStore } from '../store/use-report-layout-store'
import { CustomizeReveal } from './customize-reveal'
import './customize.css'

// Figma elevation — outer tray + inner white card.
const OUTER_SHADOW = '0px 4px 16px -2px rgba(16,24,40,0.03), 0px 1px 4px -2px rgba(16,24,40,0.02)'
const INNER_SHADOW = '0px 4px 8px rgba(16,24,40,0.03), 0px 1px 2px rgba(16,24,40,0.02)'

interface ReportSectionProps {
	/** Optional leading 16px icon (gray). Pass an Untitled UI icon. */
	icon?: ReactNode
	/** Section title. Editable in place via CanvasSpaceTitle. */
	title: string
	/** Controlled rename handler. Omit to keep the rename local to this section. */
	onTitleChange?: (next: string) => void
	/** Info-icon tooltip text. */
	info?: string
	/** Optional right-aligned header content (e.g. a search input). */
	headerAction?: ReactNode
	/**
	 * When provided, the customize-mode section pencil opens this handler (e.g. a
	 * customization drawer). The pencil only shows while in Customize mode.
	 */
	onCustomize?: () => void
	children: ReactNode
}

export const ReportSection = ({ icon, title, onTitleChange, info, headerAction, onCustomize, children }: ReportSectionProps) => {
	const [localTitle, setLocalTitle] = useState(title)
	const value = onTitleChange ? title : localTitle
	// Section rename is gated behind Customize mode (shared layout store); the
	// pencil action only appears while customizing.
	const editMode = useReportLayoutStore((s) => s.editMode)

	return (
		<section
			className="pa-report-section flex flex-col overflow-hidden rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-canvas))]"
			style={{ boxShadow: OUTER_SHADOW }}
		>
			{/* Header on the gray tray. */}
			<div className="flex items-center gap-1 py-1.5 px-1.5">
				{/* Icon (optional) + title at 2px. */}
				<div className="flex min-w-0 items-center gap-[2px]">
					{icon && (
						<span className="flex h-4 w-4 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-4 [&>svg]:w-4">
							{icon}
						</span>
					)}
					<CanvasSpaceTitle
						className="report-section-title"
						value={value}
						onValueChange={onTitleChange ?? setLocalTitle}
						editable={editMode}
						size="md"
					/>
				</div>
				{info && (
					<span
						className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3.5 [&>svg]:w-3.5"
						title={info}
						aria-label={info}
					>
						<InfoCircle />
					</span>
				)}
				{/* Right-aligned header: optional action (e.g. search) + the
				    customize-mode section pencil (Figma 6348:133588). */}
				<div className="ml-auto flex items-center gap-2">
					{headerAction}
					{onCustomize && (
						<CustomizeReveal show={editMode}>
							<IconButton size="sm" color="gray" aria-label="Customize section" icon={<Edit05 />} onClick={onCustomize} />
						</CustomizeReveal>
					)}
				</div>
			</div>
			{/* White content card (Figma 6324:134900): own border + rounded + shadow,
			    set off from the tray by a hairline (6px) so the two borders read as
			    two distinct lines — not a doubled border, not a wide gutter. */}
			<div>
				<div
					className="overflow-hidden rounded-xl border-t border-[rgb(var(--cg-border))] bg-white"
					style={{ boxShadow: INNER_SHADOW }}
				>
					{children}
				</div>
			</div>
		</section>
	)
}
